import type { DocumentNode } from 'graphql';
import { omit } from 'lodash';

import { type Job, JOB_ERROR_CODE, JOB_STATUS, type JobStatus } from 'src/data/job';
import type { ID } from 'src/lib/collection';
import { parseJson, promisedWait } from 'src/lib/common';
import { AbortError, JobError } from 'src/lib/errors';
import { captureMessage } from 'src/lib/sentry';

import { queryJobResult } from './queryJobResult';
import { queryJobStatus } from './queryJobStatus';

const DEFAULT_POLLING_INTERVAL = 3000;

interface JobProgressData {
  jobId: ID;
  status: JobStatus;
  step?: number;
  result: unknown;
  entityResult?: unknown;
}

interface WaitForJobOptions {
  abortSignal?: AbortSignal;
  pollingInterval?: number;
  onProgress?: OnProgressCallback;
  getErrorMessage?: (code: string, message: string) => string | undefined;
  /** How long to keep polling a job that has not finished, in milliseconds. No limit when unset. */
  maxWait?: number;
}

type OnProgressCallback = (data: JobProgressData) => void | Promise<void>;

const unwrapError = (response: Job): { code: string; message: string } => {
  const code = response.processingErrors?.[0]?.code ?? JOB_ERROR_CODE.generalError;
  const message = response.processingErrors?.[0]?.message ?? '';
  return { code, message };
};

const pollJob = async <T>(
  id: ID,
  options: WaitForJobOptions | undefined,
  deadline: number | undefined
): Promise<T | undefined> => {
  if (options?.abortSignal?.aborted) {
    throw new AbortError();
  }

  const response = await queryJobStatus({ id }, { abortSignal: options?.abortSignal });
  if (!response) {
    throw new JobError('Job not found', JOB_ERROR_CODE.generalError);
  }

  const result = parseJson<T>(response.result as string);

  if (response.status === JOB_STATUS.completed) {
    return result;
  }

  if (response.status === JOB_STATUS.failed) {
    const { message, code } = unwrapError(response);
    if (typeof options?.getErrorMessage === 'function') {
      const newMessage = options.getErrorMessage(code, message);
      if (newMessage) {
        throw new JobError(newMessage, code);
      }
    }
    throw new JobError(message, code);
  }

  if (typeof options?.onProgress === 'function') {
    await options.onProgress({
      jobId: id,
      status: response.status,
      step: response.step,
      result,
    });
  }

  if (deadline !== undefined && Date.now() >= deadline) {
    captureMessage('Stopped waiting for a job that did not finish', {
      extra: { jobId: id, status: response.status, maxWait: options?.maxWait },
    });
    throw new JobError('The job did not finish in time', JOB_ERROR_CODE.timeout);
  }

  await promisedWait(options?.pollingInterval ?? DEFAULT_POLLING_INTERVAL);
  return pollJob<T>(id, options, deadline);
};

/**
 * Wait for an async job on the backend to finish.
 */
export const waitForJob = async <T = unknown>(
  id: ID,
  options?: WaitForJobOptions
): Promise<T | undefined> =>
  pollJob<T>(
    id,
    options,
    options?.maxWait === undefined ? undefined : Date.now() + options.maxWait
  );

interface WaitForJobWithEntityResultOptions extends WaitForJobOptions {
  list?: boolean;
}

/**
 * Wait for job to complete and return the entity result using the given fragment.
 */
export const waitForJobWithEntityResult = async <T = unknown>(
  id: ID,
  gqlFragment: DocumentNode,
  options?: WaitForJobWithEntityResultOptions
): Promise<T | T[] | undefined> => {
  const jobOptions = options ? omit(options, 'list') : undefined;

  let wrappedOnProgress: OnProgressCallback | undefined;
  if (typeof jobOptions?.onProgress === 'function') {
    const originalOnProgress = jobOptions.onProgress;
    let progressStep: number | undefined;

    wrappedOnProgress = async (data) => {
      if (data.step == undefined) {
        return;
      }
      if (progressStep !== data.step) {
        progressStep = data.step;
        const entityResult = await queryJobResult<T>({ id }, gqlFragment, { list: options?.list });
        await originalOnProgress({
          ...data,
          entityResult,
        });
      }
    };
  }

  const finalJobOptions: WaitForJobOptions | undefined = jobOptions
    ? {
        ...jobOptions,
        onProgress: wrappedOnProgress,
      }
    : undefined;

  await waitForJob(id, finalJobOptions);

  return queryJobResult<T>({ id }, gqlFragment, { list: options?.list });
};
